/* ══════════════════════════════════════════════════════════════════════
   WorkflowStrip — SEOintent's signature "one system" graphic.

   Five coded step-tiles connected by animated flow lines, mirroring the
   real pipeline: Keyword Intel → Clusters → AI Content → Publish → Cited.
   Pure SVG icons + CSS. On desktop it's a horizontal flow; on mobile the
   tiles stack with vertical connectors. This is the SEOintent answer to
   SEOmatic's "data → pages" transformation visual.
   ════════════════════════════════════════════════════════════════════ */

const RED = "#FF2F2F";
const BLUE = "#002FA7";

const STEPS = [
  {
    n: "01", title: "Keyword Intel", desc: "Real search demand, scored by difficulty & intent.",
    icon: (
      <><circle cx="11" cy="11" r="7" /><path d="m21 21-4.3-4.3" /></>
    ),
  },
  {
    n: "02", title: "Smart Clusters", desc: "Auto-grouped into topical authority maps.",
    icon: (
      <><circle cx="5" cy="6" r="2.5" /><circle cx="19" cy="6" r="2.5" /><circle cx="12" cy="18" r="2.5" /><path d="M7 7.5 10.5 16M17 7.5 13.5 16M6.5 6h11" /></>
    ),
  },
  {
    n: "03", title: "AI Content", desc: "Drafted with Claude, optimized to RankMath 90+.",
    icon: (
      <><path d="M14 3H7a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2V8z" /><path d="M14 3v5h5" /><path d="m9.5 14 1 1 2.5-2.5" /></>
    ),
  },
  {
    n: "04", title: "1-Click Publish", desc: "Straight to WordPress & Shopify, schema included.",
    icon: (
      <><path d="M12 15V4" /><path d="m7 9 5-5 5 5" /><path d="M5 20h14" /></>
    ),
  },
  {
    n: "05", title: "Cited by AI", desc: "Tracked across ChatGPT, Gemini, Claude & Google.", accent: true,
    icon: (
      <><path d="M12 2a9 9 0 0 0-9 9c0 4 2.5 7 6 8l3 3 3-3c3.5-1 6-4 6-8a9 9 0 0 0-9-9Z" /><path d="M9 11h.01M15 11h.01" /></>
    ),
  },
];

function StepTile({ step, i }) {
  const accent = step.accent;
  return (
    <div
      className="group relative flex-1"
      style={{ animation: `hero-in 0.6s ease-out ${0.1 + i * 0.09}s both` }}
    >
      <div
        className={`relative flex h-full flex-col rounded-2xl border p-5 transition-all duration-200 ${
          accent
            ? "border-[#FF2F2F]/30 bg-[#FF2F2F]/[0.04]"
            : "border-zinc-200 bg-white hover:border-[#FF2F2F]/30 hover:shadow-lg"
        }`}
      >
        <div className="mb-4 flex items-center justify-between">
          <span
            className="flex h-11 w-11 items-center justify-center rounded-xl"
            style={{ background: accent ? RED : "rgba(255,47,47,0.10)" }}
          >
            <svg viewBox="0 0 24 24" className="h-[22px] w-[22px]" fill="none"
              stroke={accent ? "#fff" : RED} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              {step.icon}
            </svg>
          </span>
          <span className="font-mono-data text-[11px] font-bold tracking-widest text-zinc-300">{step.n}</span>
        </div>
        <h3 className="font-cabinet text-[15px] font-bold tracking-tight text-zinc-900">{step.title}</h3>
        <p className="mt-1.5 text-[13px] leading-relaxed text-zinc-500">{step.desc}</p>
      </div>

      {/* connector to next tile (desktop only) */}
      {i < STEPS.length - 1 && (
        <div aria-hidden="true" className="absolute right-0 top-1/2 z-10 hidden translate-x-1/2 -translate-y-1/2 lg:block">
          <svg width="34" height="24" viewBox="0 0 34 24" fill="none">
            <line x1="2" y1="12" x2="26" y2="12" stroke={RED} strokeOpacity="0.35" strokeWidth="2"
              strokeDasharray="4 4" className="animate-flow-dash" />
            <path d="m24 6 8 6-8 6" stroke={RED} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" fill="none" />
          </svg>
        </div>
      )}
    </div>
  );
}

export default function WorkflowStrip() {
  return (
    <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:flex lg:items-stretch lg:gap-8">
      {STEPS.map((step, i) => (
        <StepTile key={step.n} step={step} i={i} />
      ))}
    </div>
  );
}
